import { CardImage, Pricing } from '.';
import { isMobile } from 'react-device-detect';
import { useEffect, useRef } from 'react';
import { MouseEvent } from 'react';
import { SearchItemType } from '@/types/api';
import usePLPData from '@/app/(plp)/_utils/use-plp-data';
import { useUserToken } from '@/app/_utils/session';
import { Binoban } from '@/utils/binoban';
import { sendCardDataClickEvent } from '@/utils/send-card-data-click-event';
import { Flags, GiftFlag } from './flag';
import { PLPCardProps } from './types';
import { AppNextLink } from '../app-next-link';
import { getProductURL } from '../navigation/utils/url-helpers';
import { handleShowPageLoading } from '../page-loading/utils/show-loading';
let seenProductBidIds = new Set<string | number | undefined>();
let lastQueryId: string | null = null;
export const PLPCard = (product: PLPCardProps) => {
  const [ut] = useUserToken();
  const cardRef = useRef<HTMLAnchorElement>(null);
  const {
    pageResponse
  } = usePLPData();
  const queryId = pageResponse?.queryId;
  const {
    ad,
    pricingProps,
    brand,
    name,
    slug,
    image,
    isLazy,
    colorsCount,
    flags,
    hasGift = false,
    isSalable,
    id,
    indexInAllPages
  } = product;
  useEffect(() => {
    if (queryId && lastQueryId !== queryId) {
      seenProductBidIds = new Set();
      lastQueryId = queryId;
    }
  }, [queryId]);
  useEffect(() => {
    const node = cardRef.current;
    if (!node) return;
    if (seenProductBidIds.has(ad?.bidId)) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && entry.intersectionRatio === 1) {
        if (ad) {
          Binoban.adsImpressionedEvent(ad?.index ?? null, ad?.bidId ?? null);
          seenProductBidIds.add(ad?.bidId);
        }
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 1.0
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [ad?.bidId, ad]);
  const handleClick = (event: MouseEvent) => {
    if (ad) Binoban.adsClickedEvent(ad?.index ?? null, ad?.bidId ?? null);
    sendCardDataClickEvent(ut, id, queryId ?? '', SearchItemType.Product, indexInAllPages);
    if (!event.ctrlKey) handleShowPageLoading();
  };
  const imageSize = isMobile ? 140 : 156;
  return <AppNextLink prefetch={false} href={getProductURL(slug || '')} className='relative flex h-full w-full flex-col gap-2 p-2' onClick={handleClick} ref={cardRef} data-sentry-element="AppNextLink" data-sentry-component="PLPCard" data-sentry-source-file="plp-card.tsx">
            <Flags flags={flags} isAd={!!ad} data-sentry-element="Flags" data-sentry-source-file="plp-card.tsx" />
            {hasGift && <GiftFlag />}
            <div className='relative flex grow flex-col gap-1'>
                <CardImage alt={name} image={image} srcSet={{
        desktop: 130,
        mobile: 175
      }} isLazy={isLazy} imageClassName='h-auto w-35 aspect-square lg:w-39' width={imageSize} height={imageSize} data-sentry-element="CardImage" data-sentry-source-file="plp-card.tsx" />

                <div className='flex h-full flex-col gap-0.5'>
                    <span className='text-caption-12 text-text-darkGray lg:text-body-14'>{brand}</span>

                    <h3 className='line-clamp-2 text-caption-12 text-text-black lg:text-body-14'>{name}</h3>

                    {colorsCount && colorsCount > 1 && <span className='mr-auto mt-auto text-caption-10 leading-4 text-text-darkGray lg:text-caption-12 lg:leading-5'>
                            {colorsCount} رنگ
                        </span>}
                </div>
            </div>
            {isSalable ? <Pricing {...pricingProps} className='mr-auto' /> : <span className='mr-auto text-caption-12 font-medium lg:px-5 lg:text-body-14'>ناموجود</span>}
        </AppNextLink>;
};